/* FileDetailsViewRenderer.js */

"use strict";
const _ = require("lodash");
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;

require("moment-duration-format");

const readOnlyFieldIds = ["createdOn", "createdBy", "updatedBy", "updatedOn"];

class FileDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("file-details-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._formsComponent = new FormsComponent();

        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SaveFileDetails",
            handler: (this._onFileDetailsSave = this._onFileDetailsSave.bind(this))
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelFileDetails",
            handler: (this._onFileDetailsCancel = this._onFileDetailsCancel.bind(this))
        });

        this._renderingForm = false;
        this._toggleActions(model.isDirty);
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
        this._model.on("FilePropertiesChanged", (this._onFilePropertiesChanged = this._onFilePropertiesChanged.bind(this)));
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(this._onFormsPendingPatchesChanged.bind(this), 500, false))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("Dirty", this._onDirty);
        this._model.removeListener("FilePropertiesChanged", this._onFilePropertiesChanged);
        this._formsComponent.removeListener("PendingPatchesChanged", this._onFormsPendingPatchesChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._onFormsPendingPatchesChanged.flush();
        let promArr = [this._formsComponent.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({ error: err }, "FileDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
        }
        return this._model.whenModelIsReady().then(
            () => this._renderFileDetails(),
            (err) => err
        );
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _showViewActions() {
        this._model.gameEditor.showViewAction("SaveFileDetails");
        this._model.gameEditor.showViewAction("CancelFileDetails");
    }

    _hideViewActions() {
        this._model.gameEditor.hideViewAction("SaveFileDetails");
        this._model.gameEditor.hideViewAction("CancelFileDetails");
    }

    _disableViewActions() {
        this._model.gameEditor.disableViewAction("SaveFileDetails");
        this._model.gameEditor.disableViewAction("CancelFileDetails");
    }

    _enableViewActions() {
        this._model.gameEditor.enableViewAction("SaveFileDetails");
        this._model.gameEditor.enableViewAction("CancelFileDetails");
    }

    _toggleActions(state) {
        if (state) {
            this._showViewActions();
        } else {
            this._hideViewActions();
        }
    }
    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderFileDetails() {
        return Promise.resolve().then(
            () => {
                let jtype = this._getJtype();
                let pristineValue = this._getPristineValue();
                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(pristineValue, true);

                this._renderingForm = true;
                return this._formsComponent.renderer.render().then(() => {
                    this._renderingForm = false;
                });
            },
            (err) => {
                this._logger.warn({ error: err }, "FileDetailsViewRenderer._renderFileDetails() error.");
            }
        );
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onFormsPendingPatchesChanged() {
        if (!this._renderingForm) {
            let form = this._formsComponent.getValue(),
                formName = form.name;
            this._model.osImage.setName(formName);
            this._model.osImage.setVendor(form.vendor);
            this._model.osImage.setFileType(form.fileType);
            this._model.osImage.setImageType(form.imageType);
        }
    }

    _onFilePropertiesChanged(pristineProperties, before) {
        if (!this._renderingForm) {
            let formVal = this._formsComponent.getValue(); // Data from Jtype form
            let filePropsVal = this._getPristineValue(); // Data from _pristineProperties i.e. sync'd with BE
            let isDirty = this._model.isDirty;

            let updateReadOnlyFields = () => {
                _.keys(filePropsVal).map((id) => {
                    if (readOnlyFieldIds.indexOf(id) === -1) {
                        filePropsVal[id] = formVal[id];
                    }
                });
                this._formsComponent.setPristineValue(filePropsVal);
                return Promise.resolve();
            };

            if (isDirty) {
                let isReadOnlyUpdate = true;
                _.keys(pristineProperties).map((id) => {
                    if (
                        readOnlyFieldIds.indexOf(id) === -1 &&
                        !_.isEqual(_.isEmpty(pristineProperties[id]) ? "" : pristineProperties[id], _.isEmpty(before[id]) ? "" : before[id])
                    ) {
                        isReadOnlyUpdate = false;
                    }
                });
                if (!isReadOnlyUpdate) {
                    this._model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message: "This File has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text"
                            }
                        },
                        (this._onFileDetailsCancelAction = this._onFileDetailsCancelAction.bind(this)),
                        updateReadOnlyFields
                    );
                } else {
                    updateReadOnlyFields();
                }
            } else {
                this._formsComponent.setPristineValue(filePropsVal);
            }
        }
    }

    _onDirty(dirtyState) {
        this._toggleActions(dirtyState);
    }

    _onFileDetailsSave() {
        this._disableViewActions();
        return this._model
            .saveFile()
            .then(() => {
                this._enableViewActions();
            })
            .catch((/*err*/) => {
                this._enableViewActions();
            });
    }

    _onFileDetailsCancel() {
        this._model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text"
                }
            },
            this._onFileDetailsCancelAction.bind(this)
        );
    }

    _onFileDetailsCancelAction() {
        this.model.resetFileProperties();
        return this.render();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getPristineValue() {
        return this._model.osImagePristineValues;
    }
    _getJtype() {
        return this._model.osImageJType;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = FileDetailsViewRenderer;
